import { Box, CircularProgress, Fade, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

import { semanticColors } from '@/theme/tokens';

/** Lifecycle of one settings group's write, surfaced inline next to its fields. */
export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

interface SaveStatusProps {
  state: SaveState;
  /** Message for the `error` state; the caller owns the wording. */
  error?: string | null;
  savingLabel: string;
  savedLabel: string;
}

/**
 * Inline confirmation that sits in the row it belongs to, so the answer to
 * "did that stick?" is next to the control the user just touched rather than
 * in a corner of the page an on-screen keyboard can cover.
 */
export const SaveStatus = ({ state, error, savingLabel, savedLabel }: SaveStatusProps) => (
  // The live region stays mounted so assistive tech announces each transition
  // instead of treating it as a whole new node appearing.
  <Box role="status" aria-live="polite" aria-atomic="true" sx={{ display: 'flex', minWidth: 0 }}>
    <Fade in={state !== 'idle'} timeout={{ enter: 140, exit: 260 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        {state === 'saving' && (
          <>
            <CircularProgress size={12} sx={{ color: semanticColors.textTertiary }} />
            <Typography sx={{ fontSize: '12px', color: semanticColors.textTertiary }}>
              {savingLabel}
            </Typography>
          </>
        )}
        {state === 'saved' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              backgroundColor: semanticColors.successBackground,
              borderRadius: '999px',
              px: 1,
              py: '3px',
            }}
          >
            <CheckIcon sx={{ fontSize: 13, color: semanticColors.successAction }} />
            <Typography
              sx={{ fontSize: '12px', fontWeight: 600, color: semanticColors.successAction }}
            >
              {savedLabel}
            </Typography>
          </Box>
        )}
        {state === 'error' && !!error && (
          <Typography sx={{ fontSize: '12px', color: semanticColors.danger }}>{error}</Typography>
        )}
      </Box>
    </Fade>
  </Box>
);

export default SaveStatus;
